import { get } from 'svelte/store';

import type { SettingsConfig } from 'src/types/Settings';

import { user_data } from 'src/stores/account/user';
import { app, os } from 'src/stores/session/native-bridge';

import { isActiveHomeShared } from 'src/helpers/houses/homes_helpers';
import { isShellyMobileApp } from 'src/helpers/settings/settings_config_helpers';
import { isSubUnit } from 'src/helpers/user_helpers';

// Shared components
import AppVersion from 'src/components/Settings/AppSettings/AppVersion.svelte';

// Legacy components
import BackgroundScan from 'src/components/Settings/AppSettings/BackgroundScan.svelte';
import CarPlay from 'src/components/Settings/AppSettings/CarPlay/CarPlay.svelte';
import LocalControl from 'src/components/Settings/AppSettings/LocalControl.svelte';
import Wearables from 'src/components/Settings/AppSettings/Wearables.svelte';

// V2 components
import BackgroundScanV2 from 'src/components/Settings/AppSettings/BackgroundScanV2.svelte';
import CarPlayV2 from 'src/components/Settings/AppSettings/CarPlay/CarPlayV2.svelte';
import LocalControlV2 from 'src/components/Settings/AppSettings/LocalControlV2.svelte';
import WearablesV2 from 'src/components/Settings/AppSettings/WearablesV2.svelte';
import ExperimentalFeaturesV2 from 'src/components/Settings/AccountSettings/ExperimentalFeaturesV2.svelte';
import NotificationMenuV2 from 'src/components/Settings/AccountSettings/Notification/NotificationMenuV2.svelte';
import AppBackgroundV2 from 'src/components/Settings/AppSettings/AppBackgroundV2.svelte';
import LockPinV2 from 'src/components/Settings/AppSettings/LockPinV2.svelte';
import MainMenuSettings from 'src/components/Settings/AppSettings/MainMenuSettings.svelte';
import StatisticsSettingsV2 from 'src/components/Settings/AppSettings/StatisticsSettingsV2.svelte';
import SelectImageV2 from 'src/components/Settings/Tools/SelectImageV2.svelte';

const isNative = (): boolean => get(app) === true;
const isIos = (): boolean => get(os) === 'IOS';
const isNonIosNative = (): boolean => isNative() && !isIos();
const isBackgroundScanSettingVisible = (): boolean => !isActiveHomeShared();
const isInstaller = (): boolean => Boolean(get(user_data)?.installer);
const isCarPlaySettingVisible = (): boolean => isIos() && !isActiveHomeShared();

export const generalSettingsConfig: SettingsConfig = {
	legacy: [
		{
			id: 'appVersion',
			title: 'title_app_version',
			titleDefault: 'App version',
			visible: isShellyMobileApp,
			component: AppVersion,
		},
		{
			id: 'connectivity',
			title: 'app_settings_connectivity_title',
			titleDefault: 'Connectivity',
			visible: isShellyMobileApp,
			groups: [
				{
					id: 'localControl',
					title: 'app_settings_local_wifi_control_title',
					titleDefault: 'Local Wifi Control',
					component: LocalControl,
				},
				{
					id: 'backgroundScan',
					title: 'app_settings_background_scan_title',
					titleDefault: 'Background scan',
					visible: isBackgroundScanSettingVisible,
					component: BackgroundScan,
				},
			],
		},
		{
			id: 'wearables',
			title: 'title_wearables',
			titleDefault: 'Wearables',
			visible: isNonIosNative,
			component: Wearables,
		},
		{
			id: 'carPlay',
			title: 'title_car_play_devices',
			titleDefault: 'CarPlay devices',
			visible: isCarPlaySettingVisible,
			component: CarPlay,
		},
	],
	v2: [
		{
			id: 'notifications',
			title: 'collapse_title_notifications',
			titleDefault: 'Notifications',
			visible: () => !isInstaller(),
			groups: [
				{
					id: 'notificationsGroup',
					components: [{ id: 'notification', component: NotificationMenuV2 }],
				},
			],
		},
		{
			id: 'appBackground',
			title: 'title_manage_app_background',
			titleDefault: 'Manage App Background',
			subtitle: 'settings_app_background_subtitle',
			subtitleDefault: 'Manage what users can see or do in your project.',
			groups: [
				{
					id: 'appBackgroundGroup',
					title: 'title_change_app_background',
					titleDefault: 'Change app background',
					titleStyleType: 'body-small',
					hideDivider: true,
					components: [{ id: 'appBackground', component: AppBackgroundV2 }],
				},
			],
		},
		{
			id: 'privacySecurity',
			title: 'label_privacy_and_security',
			titleDefault: 'Privacy and Security',
			groups: [
				{
					id: 'lockPinGroup',
					title: 'lock_pin',
					titleDefault: 'Lock pin',
					subtitle: 'app_settings_lock_pin_description',
					subtitleDefault: 'Set up your pin',
					components: [{ id: 'lockPin', component: LockPinV2 }],
				},
			],
		},
		{
			id: 'customizations',
			title: 'settings_customisation',
			titleDefault: 'Customisation',
			subtitle: 'settings_customisation_description',
			subtitleDefault: 'Customise your uploaded images',
			titleMarginClass: 'mb-l',
			groups: [
				{
					id: 'managePicturesGroup',
					title: 'settings_manage_images',
					titleDefault: 'Manage Images',
					hideDivider: true,
					dense: true,
					components: [{ id: 'managePictures', component: SelectImageV2 }],
				},
			],
		},
		{
			id: 'personalization',
			title: 'label_personalization',
			titleDefault: 'Personalization',
			groups: [
				{
					id: 'menuOrderGroup',
					title: 'title_main_menu',
					titleDefault: 'Main menu',
					components: [{ id: 'menuOrder', component: MainMenuSettings }],
				},
				{
					id: 'statisticsGroup',
					title: 'collapse_title_statistics',
					titleDefault: 'Statistics',
					components: [{ id: 'statistics', component: StatisticsSettingsV2 }],
				},
			],
		},
		{
			id: 'appVersion',
			title: 'title_app_version',
			titleDefault: 'App version',
			visible: isShellyMobileApp,
			groups: [
				{
					id: 'appVersionGroup',
					components: [{ id: 'appVersion', component: AppVersion }],
				},
			],
		},
		{
			id: 'connectivity',
			title: 'app_settings_connectivity_title',
			titleDefault: 'Connectivity',
			subtitle: 'app_settings_connectivity_subtitle',
			subtitleDefault: 'Configure local network and background scanning settings',
			visible: isShellyMobileApp,
			groups: [
				{
					id: 'connectivityGroup',
					components: [
						{ id: 'localControl', component: LocalControlV2 },
						{
							id: 'backgroundScan',
							component: BackgroundScanV2,
							visible: isBackgroundScanSettingVisible,
						},
					],
				},
			],
		},
		{
			id: 'wearables',
			title: 'title_wearables',
			titleDefault: 'Wearables',
			visible: isNonIosNative,
			component: WearablesV2,
		},
		{
			id: 'carPlay',
			title: 'title_car_play_devices',
			titleDefault: 'CarPlay devices',
			subtitle: 'app_settings_car_play_description',
			subtitleDefault: 'Configure which devices and actions are available in CarPlay.',
			visible: isIos,
			component: CarPlayV2,
		},
		{
			id: 'experimentalFeaturesSection',
			title: 'label_experimental_features',
			titleDefault: 'Shelly Labs',
			subtitle: 'settings_shelly_labs_subtitle',
			subtitleDefault: 'Try experimental features that are still in development.',
			visible: () => !isInstaller() && !isSubUnit(),
			groups: [
				{
					id: 'experimentalFeaturesGroup',
					title: 'collapse_title_experimental_features',
					titleDefault: 'Experimental features',
					components: [
						{
							id: 'experimentalFeatures',
							component: ExperimentalFeaturesV2,
						},
					],
				},
			],
		},
	],
};
